import { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Fade,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  Checkbox,
  Grid,
  Paper,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Quiz,
  PlayArrow,
  ArrowBack,
  CheckCircle,
  NavigateNext,
  NavigateBefore,
  Info,
  Flag,
  FlagOutlined,
  AccessTime,
  CheckCircleOutline,
  EmojiEvents,
  Pause,
  PlayCircleOutline,
  RestoreOutlined,
  Security,
  Assignment,
  MenuBook,
} from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from "../../api";
import useExamMode from "../../hooks/useExamMode";
import { parseDurationSec, formatTime } from "../../utils/time";
import { resolveQuestionImageUrl } from "../../utils/mediaUrl";
import { parseBlanks } from "../../utils/parseBlanks";
import HtmlWithLatex from "../QuestionBank/components/HtmlWithLatex";
import PassageWithLineNumbers from "../QuestionBank/components/PassageWithLineNumbers";
import { computePassageWrapWidth } from "../QuestionBank/utils/passageLineExtract";
import { PreviewLatex } from "../../components/PreviewLatex";
import useContentVerification, { formatVerifierButtonLabel, getVerifierButtonColors } from "../../hooks/useContentVerification";
import QuestionVerifyDialog from "../../components/QuestionVerifyDialog";
import QuestionBankQuestionEditDialog from "../QuestionBank/components/QuestionBankQuestionEditDialog";
import { testerHasPermission } from "../../utils/testerPermissions";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

function flagQuillImageHandler() {
  const quill = this.quill;
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/png,image/jpeg,image/webp,image/gif');
  input.click();
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/student/upload-flag-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.image_url;
      if (!url) throw new Error('No image URL');
      const range = quill.getSelection(true) || { index: quill.getLength() };
      quill.insertEmbed(range.index, 'image', url, 'user');
      quill.setSelection(range.index + 1);
    } catch {
      toast.error('Image upload failed');
    }
  };
}
let _flagReasonUploading = false;
async function processFlagReasonHtml(html, setter) {
  if (!html.includes('data:image/') || _flagReasonUploading) { setter(html); return; }
  _flagReasonUploading = true;
  let processed = html;
  for (const match of [...html.matchAll(/src="(data:image\/[^"]+)"/g)]) {
    try {
      const blob = await (await fetch(match[1])).blob();
      const file = new File([blob], `flag.${blob.type.split('/')[1] || 'png'}`, { type: blob.type });
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/student/upload-flag-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data?.data?.image_url;
      if (url) processed = processed.replace(match[0], `src="${url}"`);
    } catch { toast.error('Image upload failed'); }
  }
  _flagReasonUploading = false;
  setter(processed);
}
const FLAG_QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link', 'image'],
      ['clean'],
    ],
    handlers: { image: flagQuillImageHandler },
  },
};
const FLAG_QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'script', 'list', 'bullet', 'indent', 'link', 'image'];

/** Seeded shuffle for the global fill-blank word bank (stable for a given test id). */
function shuffleWordBankItems(arr, seed) {
  const copy = [...arr];
  let s = typeof seed === "number" ? seed : String(seed).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  for (let i = copy.length - 1; i > 0; i--) {
    s = ((s * 1664525 + 1013904223) >>> 0);
    const j = s % (i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Question status constants — mirrors StudentMockExamTake pattern
const STATUS = { NOT_VISITED: 0, UNANSWERED: 1, ANSWERED: 2, FLAGGED: 3 };
const STATUS_COLORS = {
  [STATUS.NOT_VISITED]: { bg: "grey.200", border: "#9e9e9e", text: "#374151" },
  [STATUS.UNANSWERED]: { bg: "#fff3e0", border: "#ff9800", text: "#e65100" },
  [STATUS.ANSWERED]: { bg: "#e8f5e9", border: "#4caf50", text: "#2e7d32" },
  [STATUS.FLAGGED]: { bg: "#fce4ec", border: "#e91e63", text: "#880e4f" },
};

const PERFORMANCE_LEVELS = [
  { min: 80, label: "Excellent", bgcolor: "#dcfce7", color: "#166534", icon: "🏆" },
  { min: 60, label: "Good", bgcolor: "#dbeafe", color: "#1e40af", icon: "👍" },
  { min: 40, label: "Average", bgcolor: "#fef9c3", color: "#854d0e", icon: "📈" },
  { min: 0, label: "Needs Improvement", bgcolor: "#fee2e2", color: "#991b1b", icon: "💪" },
];

const getPerformanceLevel = (pct) =>
  PERFORMANCE_LEVELS.find((l) => pct >= l.min) ?? PERFORMANCE_LEVELS[PERFORMANCE_LEVELS.length - 1];

/** Laravel validation / JSON errors: prefer `message`, else first entry under `errors`. */
function getApiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
  const errs = data.errors;
  if (errs && typeof errs === "object") {
    for (const key of Object.keys(errs)) {
      const v = errs[key];
      if (Array.isArray(v) && v[0]) return String(v[0]);
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }
  return fallback;
}

// One colour per matched vocab pair (word ↔ definition)
const VOCAB_MATCH_COLORS = [
  { bg: "#dbeafe", border: "#3b82f6", text: "#1d4ed8" }, // blue
  { bg: "#d1fae5", border: "#10b981", text: "#065f46" }, // green
  { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" }, // amber
  { bg: "#fce7f3", border: "#ec4899", text: "#9d174d" }, // pink
  { bg: "#ede9fe", border: "#8b5cf6", text: "#5b21b6" }, // purple
  { bg: "#ffedd5", border: "#f97316", text: "#9a3412" }, // orange
  { bg: "#cffafe", border: "#06b6d4", text: "#155e75" }, // cyan
  { bg: "#fecaca", border: "#ef4444", text: "#991b1b" }, // red
];

// Math symbols for quick insertion in short-answer maths questions
const MATH_SYMBOLS = ['×', '÷', '²', '³', '√', '±', '≤', '≥', '≠', '≈', 'π', '∞', '°', '½', '¼', '¾'];

/**
 * Returns badge display props { label, color, bg } for a given question_type string.
 * Covers all UK 11+ subjects: English, Maths, Verbal Reasoning, Non-Verbal Reasoning.
 */
const getTypeBadgeProps = (qt = '') => {
  const t = (qt || '').toLowerCase();
  // Writing
  if (t.includes('writing') || t.includes('composition'))
    return { label: 'Writing Composition', color: '#5b21b6', bg: '#ede9fe' };
  // Fill in the blank
  if (t.includes('fill') && t.includes('blank'))
    return { label: 'Fill in the Blank', color: '#0369a1', bg: '#e0f2fe' };
  // Vocabulary matching
  if (t.includes('vocab') || t.includes('match'))
    return { label: 'Vocabulary Matching', color: '#0f766e', bg: '#ccfbf1' };
  // Short answer (both English and Maths variants)
  if (t.includes('short answer') || t === 'short-answer' || t === 'short_answer')
    return { label: 'Short Answer', color: '#92400e', bg: '#fef3c7' };
  // Diagram interpretation (Maths)
  if (t.includes('diagram'))
    return { label: 'Diagram Interpretation', color: '#1e40af', bg: '#dbeafe' };
  // Verbal — Synonym/Antonym
  if (t.includes('synonym') || t.includes('antonym'))
    return { label: 'Synonym / Antonym', color: '#86198f', bg: '#fdf4ff' };
  // Verbal — Code breaking
  if (t.includes('code break') || t.includes('code-break'))
    return { label: 'Code Breaking', color: '#9a3412', bg: '#ffedd5' };
  // Verbal — Series completion
  if (t.includes('series'))
    return { label: 'Series Completion', color: '#1d4ed8', bg: '#dbeafe' };
  // Verbal — Analogy MCQs
  if (t.includes('analogy') || t.includes('analogies'))
    return { label: 'Analogy', color: '#15803d', bg: '#dcfce7' };
  // Non-Verbal — Figure analogies
  if (t.includes('figure'))
    return { label: 'Figure Analogies', color: '#4338ca', bg: '#e0e7ff' };
  // Non-Verbal — Shape sequences
  if (t.includes('shape') || t.includes('sequence'))
    return { label: 'Shape Sequences', color: '#0e7490', bg: '#cffafe' };
  // Non-Verbal — Matrix completion
  if (t.includes('matrix'))
    return { label: 'Matrix Completion', color: '#7e22ce', bg: '#f3e8ff' };
  // Non-Verbal — Code grids
  if (t.includes('code grid') || t.includes('code-grid'))
    return { label: 'Code Grids', color: '#b45309', bg: '#fef3c7' };
  // Generic Multiple Choice — all subjects use `Multiple-choice`
  if (t.includes('multiple') || t === 'multiple-choice')
    return { label: 'Multiple Choice', color: '#166534', bg: '#dcfce7' };
  // Fallback — show raw type
  return qt ? { label: qt, color: '#374151', bg: '#f3f4f6' } : null;
};

const WorkSheetTake = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isParent = role === "parent";
  const studentId = location.state?.studentId || null;

  const basePath = isParent ? "/parent/ai-tests" : "/student/ai-tests";

  // Exam mode (testing vs actual)
  const { isTestingMode, loading: modeLoading } = useExamMode(studentId);
  const isTestModeFromNav = location.state?.isTestMode || false;
  // Use nav state only while hook is loading to avoid flicker;
  // once loaded, the hook is the authoritative server value.
  const isTestMode = modeLoading ? isTestModeFromNav : isTestingMode("worksheets");

  // Verify answers state (testing mode only)
  const [verifyingAnswers, setVerifyingAnswers] = useState(false);
  const [verifyResults, setVerifyResults] = useState({});
  const [expandedExplanations, setExpandedExplanations] = useState({});
  const [alreadyCompletedData, setAlreadyCompletedData] = useState(null);

  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const idempotencyKeyRef = useRef(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [questionStatus, setQuestionStatus] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("processing");
  const [timeLeft, setTimeLeft] = useState(0);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [flaggingQuestion, setFlaggingQuestion] = useState({});
  const [passageOpen, setPassageOpen] = useState(true);
  const [passageFontSize, setPassageFontSize] = useState(1.0);
  const isTesterWS = localStorage.getItem("role") === "tester";
  const canVerifyWSQuestion = isTesterWS && testerHasPermission("verify-question");
  const { loadVerifications: loadWSVerifications, submitVerification: submitWSVerification, getItemData: getWSItemData, submitting: wsVerifySubmitting } = useContentVerification();
  const [wsVerifyDialogOpen, setWsVerifyDialogOpen] = useState(false);
  const [wsVerifyQuestionFull, setWsVerifyQuestionFull] = useState(null);
  const [wsVerifyQuestionLoading, setWsVerifyQuestionLoading] = useState(false);
  const [wsQbEditOpen, setWsQbEditOpen] = useState(false);
  const [wsQbEditSaving, setWsQbEditSaving] = useState(false);
  // Vocab matching: { [qId]: { [wordOptId]: shuffledDefPos } }
  const [vocabMatches, setVocabMatches] = useState({});
  // Which word box is currently selected for a pending match
  const [selectedVocabWord, setSelectedVocabWord] = useState({});
  // Global fill-blank word bank: one entry per fill-blank question (correct word from options[0])
  const [wordBank, setWordBank] = useState([]);
  // Fill-in-the-blank answers: {[qId]: {instanceId, word}}
  const [fillBlankAnswers, setFillBlankAnswers] = useState({});
  // Currently dragged word bank instanceId
  const [draggingId, setDraggingId] = useState(null);
  // Stable shuffled definition order per question (computed once, never reset)
  const shuffledDefsRef = useRef({});
  const timerRef = useRef(null);
  const autoSubmittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasSavedSession, setHasSavedSession] = useState(false);
  const pausedRef = useRef(false);

  // Re-pull the questions after a tester edits one, so the change (improved stem, rewritten options,
  // new explanation) shows immediately. Only the questions array is swapped — re-running the full
  // loader would re-fire the completion check and the resume prompt mid-exam. The server is the
  // source of truth here because the worksheet option shape ({id, letter, name}) is built by this
  // endpoint and does not match what the question-bank update returns.
  const refreshWorksheetQuestions = useCallback(async () => {
    try {
      const params = isParent && studentId ? { student_id: studentId } : {};
      const res = await api.get(`${basePath}/${id}/questions`, { params });
      const fresh = res.data?.data?.test?.questions;
      if (!Array.isArray(fresh)) return;
      setTestData((prev) => (prev?.test ? { ...prev, test: { ...prev.test, questions: fresh } } : prev));
    } catch {
      // Non-critical — the edit is saved; the student just sees it on next load.
    }
  }, [basePath, id, isParent, studentId]);

  useEffect(() => {
    const handleExamModeChanged = () => {
      window.location.reload();
    };
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  // localStorage key scoped to this test + student so sessions don't collide
  const sessionKey = `ws_session_${id}_${studentId || "self"}`;

  useEffect(() => {
    const isValidId = (v) => {
      if (v == null || v === '' || v === 'null' || v === 'undefined') return false;
      const n = Number(v);
      return Number.isFinite(n) && n > 0;
    };
    if (!isValidId(id)) {
      toast.error('Invalid work sheet. Please go back and try again.');
      navigate(isParent ? '/parent/ai-answer-sheet' : '/student/ai-answer-sheet');
      return;
    }

    const fetchTestData = async () => {
      try {
        const params = isParent && studentId ? { student_id: studentId } : {};
        const response = await api.get(`${basePath}/${id}/questions`, { params });

        if (!response.data.success) {
          throw new Error(response.data.message || "Failed to fetch work sheet");
        }

        const payload = response.data.data;
        const rawStatus = payload?.test?.status ?? payload?.status ?? payload?.test_status ?? payload?.test?.test_status;
        if (rawStatus !== undefined && rawStatus !== null && Number(rawStatus) !== 2) {
          toast.error("This work sheet is currently inactive.");
          navigate(isParent ? "/parent/ai-answer-sheet" : "/student/ai-answer-sheet");
          return;
        }

        const alreadyCompleted = payload.already_completed === true || payload.test?.is_completed == 1;
        const existingSubmission = payload.submission ?? null;
        if (alreadyCompleted || existingSubmission) {
          setAlreadyCompletedData({
            name: payload.test?.name || "",
            best_score: payload.best_score ?? existingSubmission?.score ?? null,
            total_marks: payload.test?.total_marks ?? null,
            best_percentage: payload.best_percentage ?? existingSubmission?.percentage ?? null,
            total_attempts: payload.total_attempts ?? payload.attempts_count ?? 1,
            status: existingSubmission?.performance_level ?? existingSubmission?.status ?? payload.performance_level ?? null,
            submission_id: existingSubmission?.id ?? null,
          });
          return;
        }

        setTestData(payload);
        // Check for a saved in-progress session
        try {
          const saved = localStorage.getItem(`ws_session_${id}_${(isParent && studentId) ? studentId : "self"}`);
          if (saved) {
            const session = JSON.parse(saved);
            // Only offer to resume if saved within 24 hours
            if (Date.now() - (session.savedAt || 0) < 24 * 3600 * 1000) {
              setHasSavedSession(true);
            } else {
              localStorage.removeItem(`ws_session_${id}_${(isParent && studentId) ? studentId : "self"}`);
            }
          }
        } catch (_) {}
      } catch (error) {
        console.error("Work sheet fetch error:", error);
        const httpStatus = error.response?.status;
        const back = isParent ? "/parent/ai-answer-sheet" : "/student/ai-answer-sheet";
        let toastMsg;
        if (httpStatus === 401) {
          toastMsg = "Session expired. Please login again.";
        } else if (httpStatus === 403 || httpStatus === 404) {
          // Always show user-friendly message for 404/403; never leak raw model-not-found text
          const apiMsg = error.response?.data?.message || "";
          toastMsg = apiMsg && !apiMsg.toLowerCase().includes("no query results") && !apiMsg.toLowerCase().includes("model [")
            ? apiMsg
            : "Work sheet not found.";
        } else {
          toastMsg = getApiErrorMessage(error, "Failed to load work sheet. Please try again.");
        }
        toast.error(toastMsg);
        navigate(back);
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();
  }, [id, isParent, studentId, basePath, navigate]);

  const handleStartTest = () => {
    const questions = testData?.test?.questions ?? [];
    if (questions.length) {
      const initial = {};
      questions.forEach((q, idx) => {
        initial[q.id] = idx === 0 ? STATUS.UNANSWERED : STATUS.NOT_VISITED;
      });
      setQuestionStatus(initial);

      if (!isTestMode) {
        const rawTotal = questions.reduce((sum, q) => sum + parseDurationSec(q.duration_in_sec), 0);
        const totalSeconds = Math.max(60, Math.min(7200, rawTotal || 900));
        setTimeLeft(totalSeconds);
      } else {
        setTimeLeft(0);
      }

      const bank = [];
      questions.forEach((q) => {
        const t = (q.question_type || "").toLowerCase();
        const isFb = (t.includes("fill") && t.includes("blank")) || t === "fill-in-the-blank";
        if (!isFb) return;
        const opts = q.options || [];
        const name = (opts[0]?.name || "").trim();
        if (!name) return;
        bank.push({
          instanceId: `wb-${q.id}-${bank.length}`,
          word: name,
          usedByQId: null,
        });
      });
      setWordBank(shuffleWordBankItems(bank, id));
      setFillBlankAnswers({});
    }
    idempotencyKeyRef.current = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    setTestStarted(true);
  };

  // ── Session persistence ────────────────────────────────────────────────────
  const saveProgress = useCallback(() => {
    try {
      const session = {
        answers,
        textAnswers,
        vocabMatches,
        shuffledDefs: shuffledDefsRef.current,
        wordBank,
        fillBlankAnswers,
        timeLeft,
        currentQuestionIndex,
        questionStatus,
        flaggedQuestions: [...flaggedQuestions],
        savedAt: Date.now(),
      };
      localStorage.setItem(sessionKey, JSON.stringify(session));
    } catch (_) {}
  }, [answers, textAnswers, vocabMatches, wordBank, fillBlankAnswers, timeLeft, currentQuestionIndex, questionStatus, flaggedQuestions, sessionKey]);

  const handlePause = useCallback(() => {
    if (isPaused || !testStarted) return;
    pausedRef.current = true;
    setIsPaused(true);
    saveProgress();
    toast.info("Test paused. Progress saved.");
  }, [isPaused, testStarted, saveProgress]);

  const handleResume = useCallback(() => {
    if (!isPaused) return;
    pausedRef.current = false;
    setIsPaused(false);
    toast.success("Test resumed.");
  }, [isPaused]);

  const handleContinue = useCallback(() => {
    try {
      const saved = localStorage.getItem(sessionKey);
      if (!saved) return;
      const session = JSON.parse(saved);
      setAnswers(session.answers || {});
      setTextAnswers(session.textAnswers || {});
      setVocabMatches(session.vocabMatches || {});
      if (session.shuffledDefs) shuffledDefsRef.current = session.shuffledDefs;
      if (Array.isArray(session.wordBank)) setWordBank(session.wordBank);
      else if (session.fillBlankBanks) setWordBank([]);
      if (session.fillBlankAnswers) setFillBlankAnswers(session.fillBlankAnswers);
      setTimeLeft(isTestMode ? 0 : (session.timeLeft || 0));
      setCurrentQuestionIndex(session.currentQuestionIndex || 0);
      setQuestionStatus(session.questionStatus || {});
      setFlaggedQuestions(new Set(session.flaggedQuestions || []));
      idempotencyKeyRef.current = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      setTestStarted(true);
      setHasSavedSession(false);
      toast.success("Session restored. Continue where you left off.");
    } catch (_) {
      toast.error("Could not restore session. Please start fresh.");
      localStorage.removeItem(sessionKey);
      setHasSavedSession(false);
    }
  }, [sessionKey, isTestMode]);

  const handleDiscardSession = useCallback(() => {
    localStorage.removeItem(sessionKey);
    setHasSavedSession(false);
  }, [sessionKey]);

  const EMPTY_WS_FLAG_DIALOG = { open: false, questionId: null, reason: '', issue_question: false, issue_answer: false, issue_image: false };
  const [wsFlagDialog, setWsFlagDialog] = useState(EMPTY_WS_FLAG_DIALOG);

  const handleFlagQuestion = (questionId) => {
    const isFlagged = flaggedQuestions.has(questionId);
    if (isFlagged) {
      // Unflag immediately — no dialog needed
      doFlagApiCall(questionId, true, {});
    } else {
      // Open dialog before flagging
      setWsFlagDialog({ ...EMPTY_WS_FLAG_DIALOG, open: true, questionId });
    }
  };

  const doFlagApiCall = async (questionId, isUnflagging, dialogPayload) => {
    try {
      setFlaggingQuestion((prev) => ({ ...prev, [questionId]: true }));
      const basePayload = {};
      if (isParent && studentId) basePayload.student_id = studentId;
      if (isUnflagging) {
        await api.delete(`${basePath}/${id}/questions/${questionId}/flag`, { data: basePayload });
        setFlaggedQuestions((prev) => {
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
        setQuestionStatus((prev) => ({
          ...prev,
          [questionId]: answers[questionId] ? STATUS.ANSWERED : STATUS.UNANSWERED,
        }));
      } else {
        await api.post(`${basePath}/${id}/questions/${questionId}/flag`, {
          ...basePayload,
          reason: dialogPayload.reason || '',
          issue_question: dialogPayload.issue_question || false,
          issue_answer: dialogPayload.issue_answer || false,
          issue_image: dialogPayload.issue_image || false,
        });
        setFlaggedQuestions((prev) => new Set([...prev, questionId]));
        setQuestionStatus((prev) => ({ ...prev, [questionId]: STATUS.FLAGGED }));
      }
      toast.success(isUnflagging ? "Question unflagged." : "Question flagged for review.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update flag."));
    } finally {
      setFlaggingQuestion((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleWsFlagSave = () => {
    const { questionId, reason, issue_question, issue_answer, issue_image } = wsFlagDialog;
    setWsFlagDialog(EMPTY_WS_FLAG_DIALOG);
    doFlagApiCall(questionId, false, { reason, issue_question, issue_answer, issue_image });
  };

  // ── Fill-blank word bank helpers ──────────────────────────────────────────

  /** Place a word from the bank into a question's blank slot. */
  const placeFillBlankAnswer = (instanceId, qId) => {
    setWordBank((prev) => {
      const entry = prev.find((w) => w.instanceId === instanceId);
      if (!entry) return prev;

      const word = entry.word;
      const newBank = prev.map((w) => {
        if (w.usedByQId === qId && w.instanceId !== instanceId) {
          return { ...w, usedByQId: null };
        }
        if (w.instanceId === instanceId) {
          return { ...w, usedByQId: qId };
        }
        return w;
      });
      queueMicrotask(() => {
        setFillBlankAnswers((p) => ({ ...p, [qId]: { instanceId, word } }));
        setQuestionStatus((p) => ({ ...p, [qId]: STATUS.ANSWERED }));
      });
      return newBank;
    });
  };

  /** Remove a placed word from the blank and return it to the bank. */
  const releaseWord = (qId) => {
    const placed = fillBlankAnswers[qId];
    if (!placed) return;
    setWordBank((prev) =>
      prev.map((w) => (w.instanceId === placed.instanceId ? { ...w, usedByQId: null } : w)),
    );
    setFillBlankAnswers((prev) => {
      const next = { ...prev };
      delete next[qId];
      return next;
    });
    setQuestionStatus((prev) => ({
      ...prev,
      [qId]: answers[qId] ? STATUS.ANSWERED : STATUS.UNANSWERED,
    }));
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    if (testData?.test?.questions?.[index]) {
      const qId = testData.test.questions[index].id;
      setQuestionStatus((prev) => {
        if ((prev[qId] ?? STATUS.NOT_VISITED) === STATUS.NOT_VISITED) {
          return { ...prev, [qId]: STATUS.UNANSWERED };
        }
        return prev;
      });
    }
  };

  useEffect(() => {
    if (!testStarted || isTestMode) return;
    timerStartedRef.current = true;
    const timerId = setInterval(() => {
      if (pausedRef.current) return;
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    timerRef.current = timerId;
    return () => {
      clearInterval(timerId);
      timerRef.current = null;
      timerStartedRef.current = false;
    };
  }, [testStarted, isTestMode]);

  useEffect(() => {
    if (isTestMode) return;
    if (timerStartedRef.current && timeLeft === 0 && testStarted && !submitting && !autoSubmittedRef.current && testData?.test?.questions?.length) {
      autoSubmittedRef.current = true;
      clearInterval(timerRef.current);
      handleSubmitTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, testStarted, submitting]);

  // Auto-save every 30 seconds while test is active and not paused
  useEffect(() => {
    if (!testStarted || testResults) return;
    const autoSaveId = setInterval(() => {
      if (!pausedRef.current) saveProgress();
    }, 30000);
    return () => clearInterval(autoSaveId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testStarted, testResults, saveProgress]);

  // Navigation guard — save progress before unload
  useEffect(() => {
    if (!testStarted || testResults) return;
    const handler = (e) => {
      saveProgress();
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [testStarted, testResults, saveProgress]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!testStarted || showProcessing || showResultsDialog || !testData?.test?.questions?.length) return;
    if (wsVerifyDialogOpen || wsQbEditOpen || wsFlagDialog.open) return;
    const target = e.target;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    const questions = testData.test.questions;
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    if (e.key === "ArrowRight" || e.key === "n" || e.key === "N") {
      if (currentQuestionIndex < questions.length - 1) goToQuestion(currentQuestionIndex + 1);
    } else if (e.key === "ArrowLeft" || e.key === "p" || e.key === "P") {
      if (currentQuestionIndex > 0) goToQuestion(currentQuestionIndex - 1);
    } else if (e.key === "f" || e.key === "F") {
      handleFlagQuestion(currentQuestion.id);
    } else if (["1", "2", "3", "4", "5"].includes(e.key)) {
      const optIndex = Number(e.key) - 1;
      const opt = currentQuestion.options?.[optIndex];
      if (opt) handleAnswerChange(currentQuestion.id, String(opt.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testStarted, showProcessing, showResultsDialog, wsVerifyDialogOpen, wsQbEditOpen, wsFlagDialog.open, testData, currentQuestionIndex]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Deep link from the Verification Dashboard's unverified-question chips.
  useEffect(() => {
    const jumpId = location.state?.jumpToQuestionId;
    const qs = testData?.test?.questions;
    if (!jumpId || !Array.isArray(qs) || qs.length === 0) return;
    const target = qs.findIndex((q) => Number(q.question_bank_question_id ?? q.id) === Number(jumpId));
    if (target >= 0) setCurrentQuestionIndex(target);
    window.history.replaceState({}, "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testData]);

  // Preload verification state for the current question so the Verify button
  // can reflect any tester's rejection without the tester having to open it.
  useEffect(() => {
    if (!canVerifyWSQuestion) return;
    const q = testData?.test?.questions?.[currentQuestionIndex];
    const bankId = q?.question_bank_question_id || q?.id;
    if (!bankId) return;
    if (getWSItemData("question", bankId)) return;
    loadWSVerifications("question", bankId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canVerifyWSQuestion, currentQuestionIndex, testData]);

  const handleAnswerChange = (questionId, optionId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
    setQuestionStatus((prev) => ({ ...prev, [questionId]: STATUS.ANSWERED }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < testData.test.questions.length - 1) {
      goToQuestion(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      goToQuestion(currentQuestionIndex - 1);
    }
  };

  const handleSubmitTest = async () => {
    setSubmitting(true);
    setShowProcessing(true);
    setProcessingStep("processing");

    try {
      const answersArray = testData.test.questions
        .map((q) => {
          const category = getQuestionCategory(q.question_type);
          const base = { question_id: q.id, answer_type: category };

          if (category === "mcq") {
            return { ...base, option_id: answers[q.id] ? parseInt(answers[q.id], 10) : null };
          }
          if (category === "vocab_matching") {
            const pairs = vocabMatches[q.id] || {};
            const order = shuffledDefsRef.current[q.id] || [];
            return {
              ...base,
              matched_pairs: Object.entries(pairs).map(([wordOptId, defPos]) => ({
                word_option_id: parseInt(wordOptId, 10),
                matched_def_index: defPos,
              })),
              shuffled_order: order,
            };
          }
          if (category === "fill_blank") {
            const placed = fillBlankAnswers[q.id];
            return { ...base, text_answer: placed ? placed.word : (textAnswers[q.id] || "") };
          }
          if (category === "reading_comprehension") {
            return { ...base, text_answer: textAnswers[q.id] || "" };
          }
          // short_answer, writing
          return { ...base, text_answer: textAnswers[q.id] || "" };
        })
        .filter((a) => a.option_id || a.text_answer?.trim() || a.matched_pairs?.length);

      if (getAnsweredCount() === 0) {
        toast.warning("Please answer at least one question before submitting.");
        setSubmitting(false);
        setShowProcessing(false);
        return;
      }

      const payload = { answers: answersArray };
      if (idempotencyKeyRef.current) payload.idempotency_key = idempotencyKeyRef.current;
      if (isParent && studentId) payload.student_id = studentId;

      const response = await api.post(`${basePath}/${id}/submit-online`, payload);

      if (response.data.success) {
        localStorage.removeItem(sessionKey);
        setProcessingStep("success");
        setTimeout(() => {
          setTestResults(response.data.data);
          setShowProcessing(false);
          setShowResultsDialog(true);
          toast.success(response.data.message || "Work sheet submitted successfully.");
        }, 800);
      } else {
        throw new Error(response.data.message || "Submit failed");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(getApiErrorMessage(error, "Failed to submit. Please try again."));
    } finally {
      setSubmitting(false);
      setShowProcessing(false);
    }
  };

  // ── Verify answers (testing mode only) ───────────────────────────────────
  const handleVerifyAnswers = async () => {
    if (!isTestMode) return;
    setVerifyingAnswers(true);
    try {
      const payload = [];
      const allQuestions = testData?.test?.questions ?? [];

      allQuestions.forEach((q) => {
        const category = getQuestionCategory(q.question_type);
        if (category === "mcq" && answers[q.id]) {
          const opt = q.options?.find((o) => String(o.id) === String(answers[q.id]));
          if (opt) payload.push({ question_id: q.id, option_id: opt.id });
        } else if (category === "fill_blank") {
          const placed = fillBlankAnswers[q.id];
          const text = placed?.word || textAnswers[q.id]?.trim();
          if (text) payload.push({ question_id: q.id, text_answer: text });
        } else if (category === "short_answer" && textAnswers[q.id]?.trim()) {
          payload.push({ question_id: q.id, text_answer: textAnswers[q.id].trim() });
        } else if (category === "vocab_matching") {
          const pairs = vocabMatches[q.id] || {};
          const order = shuffledDefsRef.current[q.id] || [];
          if (Object.keys(pairs).length > 0) {
            payload.push({
              question_id: q.id,
              matched_pairs: Object.entries(pairs).map(([wordOptId, defPos]) => ({
                word_option_id: parseInt(wordOptId, 10),
                matched_def_index: defPos,
              })),
              shuffled_order: order,
            });
          }
        }
      });

      // Always include the current question so its correct answer is shown
      const currentQ = testData?.test?.questions?.[currentQuestionIndex];
      if (currentQ && !payload.some((p) => p.question_id === currentQ.id)) {
        payload.push({ question_id: currentQ.id });
      }

      const reqBody = { answers: payload };
      if (isParent && studentId) reqBody.student_id = studentId;

      const res = await api.post(`${basePath}/${id}/verify-answers`, reqBody);
      if (res.data?.success) {
        const map = {};
        (res.data.data?.results || []).forEach((r) => {
          map[r.question_id] = r;
        });
        setVerifyResults(map);
        const pct = res.data.data?.percentage ?? null;
        toast.success(
          pct !== null
            ? `Answers verified! ${res.data.data.correct_count}/${res.data.data.total_checked} correct (${pct}%)`
            : "Answers verified! Check the inline results."
        );
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to verify answers."));
    } finally {
      setVerifyingAnswers(false);
    }
  };

  const toggleExplanation = (qId) => {
    setExpandedExplanations((prev) => ({ ...prev, [qId]: !prev[qId] }));
  };

  const getExplanationForQuestion = (qId, vrExplanation) => {
    const fromQuestion = testData?.test?.questions?.find((q) => q.id === qId)?.explanation;
    return vrExplanation || fromQuestion || null;
  };

  const explanationSx = {
    fontSize: "0.9rem",
    lineHeight: 1.7,
    color: "text.primary",
    "& ol": { margin: "6px 0", paddingLeft: "1.5rem" },
    "& ul": { margin: "6px 0", paddingLeft: "1.5rem" },
    "& li": { marginBottom: "6px" },
    "& li:last-child": { marginBottom: 0 },
    "& p": { margin: "0 0 6px" },
    "& p:last-child": { marginBottom: 0 },
  };

  const renderExplanation = (qId, explanation) => {
    if (!explanation) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="text"
          onClick={() => toggleExplanation(qId)}
          sx={{ textTransform: "none", color: "primary.main", fontWeight: 500, p: 0 }}
        >
          {expandedExplanations[qId] ? "Hide explanation" : "Show explanation"}
        </Button>
        <Collapse in={!!expandedExplanations[qId]}>
          <Box sx={{ mt: 0.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
            <PreviewLatex html={explanation} sx={explanationSx} />
          </Box>
        </Collapse>
      </Box>
    );
  };

  const handleCloseResults = () => {
    setShowResultsDialog(false);
    navigate(isParent ? "/parent/ai-answer-sheet" : "/student/ai-answer-sheet");
  };

  const handleViewReport = () => {
    setShowResultsDialog(false);
    const resultId = testResults?.id;
    if (resultId) {
      const prefix = isParent ? "/parent" : "/student";
      navigate(`${prefix}/work-sheet/${id}/attempt/${resultId}/review`, {
        state: isParent && studentId ? { studentId } : undefined,
      });
    } else {
      const path = isParent ? "/parent/ai-answer-sheet" : "/student/ai-answer-sheet";
      navigate(path, {
        state: {
          viewResult: testResults,
          viewTestId: id,
          ...(isParent && studentId ? { studentId } : {}),
        },
      });
    }
  };

  const getAnsweredCount = () => {
    const answeredIds = new Set();
    // MCQ (and fill/short soft-match selections)
    Object.keys(answers).forEach((qId) => answeredIds.add(qId));
    // fill_blank / short_answer / writing — keyed directly by qId (no underscore)
    Object.entries(textAnswers).forEach(([key, val]) => {
      if (!key.includes("_") && val?.trim() && !answers[key]) {
        try {
          const parsed = JSON.parse(val);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            if (Object.values(parsed).some((v) => String(v || "").trim())) answeredIds.add(key);
          } else if (val?.trim()) {
            answeredIds.add(key);
          }
        } catch {
          answeredIds.add(key);
        }
      }
    });
    // vocab_matching — keyed by qId; count if at least one pair is matched
    Object.entries(vocabMatches).forEach(([qId, pairs]) => {
      if (Object.keys(pairs).length > 0) answeredIds.add(String(qId));
    });
    // fill_blank drag-and-drop answers
    Object.entries(fillBlankAnswers).forEach(([qId, val]) => {
      if (val?.word) answeredIds.add(String(qId));
    });
    return answeredIds.size;
  };
  const totalQuestions = testData?.test?.questions?.length ?? 0;
  const progress = totalQuestions > 0 ? (getAnsweredCount() / totalQuestions) * 100 : 0;

  /**
   * Determine the interactive input category for a question based on its question_type string.
   * Returns: 'mcq' | 'fill_blank' | 'short_answer' | 'vocab_matching' | 'writing'
   */
  const getQuestionCategory = (qt = "") => {
    const t = qt.toLowerCase();
    if (t.includes("writing") || t.includes("composition")) return "writing";
    if (t.includes("reading") || t.includes("comprehension") || t.includes("passage")) return "reading_comprehension";
    if ((t.includes("fill") && t.includes("blank")) || t === "fill-in-the-blank") return "fill_blank";
    if (t.includes("short answer") || t === "short-answer" || t === "short_answer") return "short_answer";
    if (t.includes("vocab") || t.includes("match")) return "vocab_matching";
    return "mcq";
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <Card sx={{ p: 5, textAlign: "center", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <Avatar sx={{ width: 64, height: 64, mx: "auto", mb: 2, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              <Quiz sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Loading Work Sheet…
            </Typography>
            <LinearProgress sx={{ mt: 2, width: 220, mx: "auto", height: 6, borderRadius: 3 }} />
          </Card>
        </Box>
      </Container>
    );
  }

  if (alreadyCompletedData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", overflow: "hidden" }}>
          <Box
            sx={{
              background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
              color: "white",
            }}
          >
            <CheckCircle sx={{ fontSize: 36 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Test Already Completed
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {alreadyCompletedData.name}
              </Typography>
            </Box>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" gutterBottom>
              You have already completed the test{" "}
              <strong>"{alreadyCompletedData.name}"</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 700 }}>
              Your Performance:
            </Typography>
            <Box sx={{ mt: 1, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
              {alreadyCompletedData.best_score != null && (
                <Typography variant="body2">
                  • Best Score:{" "}
                  <strong>
                    {alreadyCompletedData.best_score}
                    {alreadyCompletedData.total_marks != null
                      ? `/${alreadyCompletedData.total_marks}`
                      : ""}
                  </strong>
                </Typography>
              )}
              {alreadyCompletedData.best_percentage != null && (
                <Typography variant="body2">
                  • Best Percentage:{" "}
                  <strong>
                    {Number(alreadyCompletedData.best_percentage).toFixed(1)}%
                  </strong>
                </Typography>
              )}
              <Typography variant="body2">
                • Total Attempts:{" "}
                <strong>{alreadyCompletedData.total_attempts}</strong>
              </Typography>
              {alreadyCompletedData.status && (
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
                >
                  • Status:{" "}
                  <Chip
                    label={alreadyCompletedData.status}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              All your attempts for this work sheet have been completed. You
              cannot take this test again.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    isParent ? "/parent/ai-answer-sheet" : "/student/ai-answer-sheet"
                  )
                }
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                Understood
              </Button>
              {alreadyCompletedData.submission_id && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    const prefix = isParent ? "/parent" : "/student";
                    navigate(
                      `${prefix}/work-sheet/${id}/attempt/${alreadyCompletedData.submission_id}/review`,
                      {
                        state:
                          isParent && studentId ? { studentId } : undefined,
                      }
                    );
                  }}
                  sx={{ borderRadius: 2, fontWeight: 600 }}
                >
                  View Report
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!testData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <Card sx={{ p: 5, textAlign: "center", maxWidth: 460, borderRadius: 3 }}>
            <Avatar sx={{ width: 64, height: 64, mx: "auto", mb: 2, bgcolor: "grey.300" }}>
              <Quiz sx={{ fontSize: 32, color: "#374151" }} />
            </Avatar>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }} color="text.secondary">
              Work Sheet Not Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This work sheet may no longer be available or you may not have access.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate(isParent ? "/parent/ai-answer-sheet" : "/student/ai-answer-sheet")}
            >
              Back to Work Sheets
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  const questions = testData.test.questions;
  const currentQuestion = questions[currentQuestionIndex];
  const wsVerifyBankId = currentQuestion?.question_bank_question_id || currentQuestion?.id;
  const questionImageUrl = resolveQuestionImageUrl(currentQuestion);
  const selectedOption = currentQuestion?.options?.find((o) => String(o.id) === String(answers[currentQuestion?.id]));
  const selectedLetter = selectedOption?.letter ?? "—";

  const sharedPassage = (() => {
    if (!questions?.length) return null;
    const first = questions[0];
    const p = first?.passage_text || first?.passage_stem || null;
    if (!p) return null;
    const cat = getQuestionCategory(first?.question_type || "");
    if (cat === "reading_comprehension") return null;
    return p;
  })();

  const currentPassageText = (() => {
    if (getQuestionCategory(currentQuestion?.question_type || "") === "reading_comprehension") {
      return currentQuestion?.passage_text || currentQuestion?.passage_stem || "";
    }
    return sharedPassage || "";
  })();

  const renderPassageBox = (text, refQuestions = []) => {
    if (!text || !passageOpen) return null;
    return (
      <Box sx={{ mb: 2.5, p: 2.5, bgcolor: "#faf8f5", borderRadius: 2, borderLeft: "4px solid #8d6e63" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <MenuBook sx={{ color: "#5d4037", fontSize: 20 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#5d4037", textTransform: "uppercase" }}>
            Reading Passage
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPassageFontSize((s) => Math.max(0.75, +(s - 0.1).toFixed(1)))}
            sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.8rem", lineHeight: 1, borderColor: "#d7ccc8", color: "#5d4037" }}
          >
            A−
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPassageFontSize((s) => Math.min(1.6, +(s + 0.1).toFixed(1)))}
            sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.8rem", lineHeight: 1, borderColor: "#d7ccc8", color: "#5d4037" }}
          >
            A+
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => setPassageOpen(false)}
            sx={{ textTransform: "none", fontSize: "0.72rem", fontWeight: 700, color: "text.secondary", minWidth: 0, px: 1 }}
          >
            Hide ✕
          </Button>
        </Box>
        <Box sx={{ maxHeight: "50vh", overflowY: "auto", pr: 1 }}>
          <PassageWithLineNumbers
            text={text}
            wrapWidth={computePassageWrapWidth(text, refQuestions)}
            fontSize={passageFontSize}
          />
        </Box>
      </Box>
    );
  };

  // Subject key for type-specific UI (math symbol bar, etc.)
  const testSubjectLower = (testData.test.subject || "").toLowerCase();
  const isMathsTest = testSubjectLower.includes("maths") || testSubjectLower.includes("math");

  // ── Start Screen ─────────────────────────────────────────────────────────
  if (!testStarted) {
    const totalSeconds = Math.max(60, questions.reduce((s, q) => s + parseDurationSec(q.duration_in_sec), 0) || 900);
    const hasWriting = questions.some((q) => { const t = (q.question_type || "").toLowerCase(); return t.includes("writing") || t.includes("composition"); });
    const hasVocab   = questions.some((q) => { const t = (q.question_type || "").toLowerCase(); return t.includes("vocab") || t.includes("match"); });
    const writingQ   = questions.find((q) => { const t = (q.question_type || "").toLowerCase(); return t.includes("writing") || t.includes("composition"); });
    const minWords   = writingQ?.ai_metadata?.min_words ?? 75;
    const uniqueTypes = [...new Set(questions.map((q) => {
      const badge = getTypeBadgeProps(q.question_type);
      return badge?.label ?? q.question_type ?? "Multiple Choice";
    }))];

    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Fade in timeout={400}>
          <Alert severity="info" icon={<Security />} sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Secure Test Environment:</strong> This test is monitored for security.
              Please do not switch tabs or leave this page during the test.
            </Typography>
          </Alert>
        </Fade>

        <Fade in timeout={600}>
          <Card sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: "20px",
            boxShadow: "0 10px 30px rgba(102,126,234,0.3)",
            mb: 4,
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: isTestMode ? 2 : 0 }}>
                <IconButton
                  aria-label="Back to Work Sheets"
                  onClick={() => navigate(isParent ? "/parent/ai-answer-sheet" : "/student/ai-answer-sheet")}
                  sx={{ background: "rgba(255,255,255,0.15)", color: "white", "&:hover": { background: "rgba(255,255,255,0.25)" } }}
                >
                  <ArrowBack />
                </IconButton>
                <Avatar sx={{ background: "rgba(255,255,255,0.2)", width: 60, height: 60 }}>
                  <Quiz sx={{ fontSize: 30 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    📝 {testData.test.name}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Work Sheet — Instructions & Overview
                  </Typography>
                </Box>
                {isTestMode && (
                  <Chip
                    label="PRACTICE MODE"
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "white", fontWeight: 700, letterSpacing: 0.5, border: "1px solid rgba(255,255,255,0.4)" }}
                  />
                )}
              </Box>
              {isTestMode && (
                <Alert severity="info" sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", borderRadius: 2, "& .MuiAlert-icon": { color: "white" } }}>
                  PRACTICE MODE — Practice freely. Answers will not be graded. You can use "Check Answers" to verify your work without submitting.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Fade>

        <Grid container spacing={4}>
          {/* Left — Instructions */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={800}>
              <Card sx={{ height: "fit-content" }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: "info.main" }}><Info /></Avatar>
                    <Typography variant="h5" fontWeight={600}>Test Instructions</Typography>
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Typography variant="body1" paragraph>
                      Welcome to the <strong>{testData.test.name}</strong>. Please read the following instructions carefully before starting:
                    </Typography>

                    <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                      {[
                        <span>This test contains <strong>{questions.length} questions</strong></span>,
                        ...(isTestMode
                          ? [
                              "Practice mode is untimed — work at your own pace",
                              'Use "Check Answers" to verify your work without submitting',
                            ]
                          : [
                              <span>Total time allocated: <strong>{formatTime(totalSeconds)}</strong></span>,
                              "Each question has a specific time limit",
                              "Once time runs out, the test will be submitted automatically",
                            ]),
                        "You can navigate between questions using Next/Previous buttons",
                        "Your progress will be saved automatically",
                        ...(hasWriting ? [<span>Writing composition requires at least <strong>{minWords} words</strong> for full marks</span>] : []),
                        ...(hasVocab ? ["Vocabulary matching: tap a word then a definition to link them"] : []),
                      ].map((point, i) => (
                        <Typography key={i} component="li" variant="body2" sx={{ mb: 1 }}>
                          {point}
                        </Typography>
                      ))}
                    </Box>

                    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "error.50", border: "1px solid", borderColor: "error.200", mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: "error.main", width: 32, height: 32 }}>
                          <Security sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6" color="error.dark">Security Guidelines</Typography>
                      </Box>
                      <Typography variant="body2" color="error.dark">
                        • Do not switch tabs or minimize the browser during the test<br />
                        • Do not use external resources or assistance<br />
                        • Ensure you have a stable internet connection<br />
                        • Complete the test in one sitting without interruption<br />
                        • Any suspicious activity may result in test termination
                      </Typography>
                    </Paper>

                    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "warning.50", border: "1px solid", borderColor: "warning.200" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: "warning.main", width: 32, height: 32 }}>
                          <Flag sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6" color="warning.dark">Important Notes</Typography>
                      </Box>
                      <Typography variant="body2" color="warning.dark">
                        • Make sure you have a stable internet connection<br />
                        • Do not refresh the page during the test<br />
                        • Answer all questions to maximize your score<br />
                        • Review your answers before final submission
                      </Typography>
                    </Paper>
                  </Box>

                  {hasSavedSession && (
                    <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: "rgba(102,126,234,0.08)", border: "1px solid rgba(102,126,234,0.3)", display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <RestoreOutlined color="primary" />
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                        You have a saved session for this test. Continue where you left off?
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" variant="contained" startIcon={<PlayCircleOutline />} onClick={handleContinue} sx={{ background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)", fontWeight: 600, borderRadius: 1.5 }}>
                          Continue Session
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={handleDiscardSession} sx={{ borderRadius: 1.5 }}>
                          Discard
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PlayArrow />}
                      onClick={handleStartTest}
                      sx={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        borderRadius: "25px",
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        boxShadow: "0 4px 15px rgba(102,126,234,0.3)",
                        "&:hover": { boxShadow: "0 6px 20px rgba(102,126,234,0.4)", transform: "translateY(-2px)" },
                      }}
                    >
                      {hasSavedSession ? "Start Fresh" : isTestMode ? "Start Practice" : "Start Test"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          {/* Right — Test Overview */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1000}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: "success.main" }}><Assignment /></Avatar>
                    <Typography variant="h6" fontWeight={600}>Test Overview</Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Total Questions</Typography>
                      <Chip label={questions.length} sx={{ bgcolor: "primary.main", color: "white" }} size="small" />
                    </Box>
                    {!isTestMode ? (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Total Duration</Typography>
                        <Chip label={formatTime(totalSeconds)} sx={{ bgcolor: "secondary.main", color: "white" }} size="small" />
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Timing</Typography>
                        <Chip label="Untimed" sx={{ bgcolor: "info.main", color: "white" }} size="small" />
                      </Box>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Question Types</Typography>
                      <Chip label={uniqueTypes.slice(0, 2).join(", ") || "Mixed"} sx={{ bgcolor: "info.main", color: "white" }} size="small" />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Security Level</Typography>
                      <Chip label="High" icon={<Security sx={{ fontSize: 14, color: "white !important" }} />} sx={{ bgcolor: "error.main", color: "white" }} size="small" />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                    Good luck with your test! 🍀
                  </Typography>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    );
  }

  // ── Active Test ───────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Gradient header — matches TopicTest style */}
      <Card sx={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "white",
        borderRadius: "15px",
        mb: 3,
      }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
            {/* Left: icon + name + question counter */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
                <Assignment />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                  {testData.test.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </Typography>
              </Box>
            </Box>

            {/* Right: timer + answered + pause + secure */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 }, flexWrap: "wrap" }}>
              {!isTestMode ? (
                timeLeft > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <AccessTime sx={{ fontSize: 20 }} />
                    <Typography variant="h6" fontWeight={600}>
                      {isPaused ? `${formatTime(timeLeft)} — Paused` : formatTime(timeLeft)}
                    </Typography>
                  </Box>
                )
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.9 }}>
                  Untimed practice
                </Typography>
              )}

              <Chip
                label={`${getAnsweredCount()}/${questions.length} Answered`}
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600 }}
              />

              {!isTestMode && (isPaused ? (
                <Chip
                  icon={<PlayCircleOutline sx={{ color: "white !important" }} />}
                  label="Resume"
                  onClick={handleResume}
                  sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "white", fontWeight: 700, cursor: "pointer" }}
                />
              ) : (
                <Chip
                  icon={<Pause sx={{ color: "white !important" }} />}
                  label="Pause"
                  onClick={handlePause}
                  sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600, cursor: "pointer" }}
                />
              ))}

              <Chip
                icon={<Security sx={{ color: "white !important" }} />}
                label="Secure"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                size="small"
              />
            </Box>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: "rgba(255,255,255,0.2)",
              "& .MuiLinearProgress-bar": { bgcolor: "white" },
            }}
          />
          {isTesterWS && testData?.test?.total_marks > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>
                Total marks — {questions.length}Q
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.95 }}>
                {testData.test.total_marks}m
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Practice mode banner */}
      {isTestMode && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleVerifyAnswers}
              disabled={verifyingAnswers}
              sx={{ fontWeight: 700 }}
            >
              {verifyingAnswers ? "Checking…" : "Check Answers"}
            </Button>
          }
        >
          PRACTICE MODE — Use "Check Answers" to verify your work without submitting.
        </Alert>
      )}

      {/* Question navigator */}
      {questions.length > 1 && (
        <Box sx={{ p: 2, mb: 2, bgcolor: "#f8f9fb", borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary", mb: 1.5 }}>
            Jump to Question
          </Typography>
          {/* Legend */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
            {[
              { label: "Not Visited", color: STATUS_COLORS[STATUS.NOT_VISITED] },
              { label: "Unanswered", color: STATUS_COLORS[STATUS.UNANSWERED] },
              { label: "Answered", color: STATUS_COLORS[STATUS.ANSWERED] },
              { label: "Flagged", color: STATUS_COLORS[STATUS.FLAGGED] },
            ].map(({ label, color }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: color.bg,
                    border: `1.5px solid ${color.border}`,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
          {/* Grid — scrollable, max 2 rows visible */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              maxHeight: 84,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 2 },
            }}
          >
            {questions.map((q, index) => {
              const isCurrent = index === currentQuestionIndex;
              const status = questionStatus[q.id] ?? STATUS.NOT_VISITED;
              const colors = STATUS_COLORS[status];
              return (
                <Button
                  key={index}
                  size="small"
                  variant={isCurrent ? "contained" : "outlined"}
                  onClick={() => goToQuestion(index)}
                  aria-label={`Go to question ${index + 1}`}
                  aria-current={isCurrent ? "true" : undefined}
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    p: 0,
                    fontSize: "0.82rem",
                    fontWeight: isCurrent ? 700 : 500,
                    borderRadius: 1.5,
                    borderColor: isCurrent ? "primary.main" : colors.border,
                    color: isCurrent ? "white" : colors.text,
                    bgcolor: isCurrent ? "primary.main" : colors.bg,
                    "&:hover": { opacity: 0.82 },
                    transition: "all 0.15s",
                  }}
                >
                  {index + 1}
                </Button>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Paused banner */}
      {!isTestMode && isPaused && (
        <Card
          sx={{
            mb: 2,
            borderRadius: 2,
            bgcolor: "warning.50",
            border: "1px solid",
            borderColor: "warning.200",
            boxShadow: "0 2px 8px rgba(237,108,2,0.12)",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Pause color="warning" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700, color: "warning.dark" }}>
                  Test Paused
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your progress has been saved. Press Resume to continue.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayCircleOutline />}
                onClick={handleResume}
                sx={{ fontWeight: 700, borderRadius: 1.5 }}
              >
                Resume
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Shared fill-blank word bank panel — only shown when current question is Fill in the Blank */}
      {testStarted && currentQuestion && getQuestionCategory(currentQuestion.question_type) === "fill_blank" && wordBank.length > 0 && (
        <Card
          sx={{
            mb: 2,
            borderRadius: 2,
            border: "2px solid",
            borderColor: "primary.300",
            boxShadow: "0 4px 16px rgba(102,126,234,0.18)",
            position: "sticky",
            top: 8,
            zIndex: 10,
            bgcolor: "background.paper",
            opacity: isPaused ? 0.4 : 1,
            pointerEvents: isPaused ? "none" : "auto",
          }}
        >
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: "primary.main",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  fontSize: "0.7rem",
                }}
              >
                Word Bank
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                — click or drag a word onto a blank
              </Typography>
              <Box sx={{ ml: "auto" }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                  {wordBank.filter((w) => w.usedByQId !== null).length}/{wordBank.length} used
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {wordBank.map((item) => {
                const used = item.usedByQId !== null;
                const isCurrentBlank =
                  used &&
                  item.usedByQId ===
                    testData?.test?.questions?.[currentQuestionIndex]?.id;
                return (
                  <Chip
                    key={item.instanceId}
                    label={item.word}
                    draggable={!used}
                    size="small"
                    onClick={() => {
                      if (used) return;
                      const currentQ = testData?.test?.questions?.[currentQuestionIndex];
                      if (currentQ && getQuestionCategory(currentQ.question_type) === "fill_blank") {
                        placeFillBlankAnswer(item.instanceId, currentQ.id);
                      }
                    }}
                    onDragStart={() => setDraggingId(item.instanceId)}
                    onDragEnd={() => setDraggingId(null)}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      height: 28,
                      cursor: used ? "default" : "grab",
                      opacity: used ? 0.4 : 1,
                      textDecoration: used ? "line-through" : "none",
                      bgcolor: isCurrentBlank
                        ? "success.50"
                        : draggingId === item.instanceId
                        ? "primary.100"
                        : used
                        ? "grey.100"
                        : "primary.50",
                      color: isCurrentBlank ? "success.dark" : used ? "text.disabled" : "primary.dark",
                      border: "1.5px solid",
                      borderColor: isCurrentBlank
                        ? "success.300"
                        : draggingId === item.instanceId
                        ? "primary.400"
                        : used
                        ? "grey.300"
                        : "primary.300",
                      transition: "all 0.15s ease",
                      "&:hover": used ? {} : { bgcolor: "primary.100", transform: "translateY(-2px)", boxShadow: "0 2px 6px rgba(102,126,234,0.25)" },
                    }}
                  />
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Question card */}
      <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", opacity: isPaused ? 0.4 : 1, pointerEvents: isPaused ? "none" : "auto" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          {/* Question header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={`Q ${currentQuestionIndex + 1}`}
                size="small"
                color="primary"
                sx={{ fontWeight: 700 }}
              />
              {isTesterWS && (() => {
                const m = currentQuestion?.marks ?? currentQuestion?.default_marks ?? currentQuestion?.answer_data?.marks ?? null;
                return m > 0 ? (
                  <Chip label={`${m}m`} size="small" sx={{ fontWeight: 700, bgcolor: "#fff3e0", color: "#e65100", fontSize: "0.72rem", height: 22 }} />
                ) : null;
              })()}
              {flaggedQuestions.has(currentQuestion?.id) && (
                <Chip
                  label="Flagged"
                  size="small"
                  color="warning"
                  icon={<Flag sx={{ fontSize: 14 }} />}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
            {currentPassageText && (
              <Tooltip title={passageOpen ? "Close reading passage" : "Show reading passage"}>
                <Button
                  size="small"
                  variant={passageOpen ? "contained" : "outlined"}
                  startIcon={<MenuBook fontSize="small" />}
                  onClick={() => setPassageOpen((prev) => !prev)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    color: passageOpen ? "white" : "#1565c0",
                    borderColor: "#1565c0",
                    bgcolor: passageOpen ? "#1565c0" : "transparent",
                    "&:hover": { bgcolor: passageOpen ? "#0d47a1" : "rgba(21,101,192,0.06)" },
                  }}
                >
                  {passageOpen ? "Close Passage" : "Show Passage"}
                </Button>
              </Tooltip>
            )}
            <Button
              size="small"
              variant={flaggedQuestions.has(currentQuestion?.id) ? "contained" : "outlined"}
              color="warning"
              startIcon={
                flaggingQuestion[currentQuestion?.id] ? (
                  <CircularProgress size={14} color="inherit" />
                ) : flaggedQuestions.has(currentQuestion?.id) ? (
                  <Flag />
                ) : (
                  <FlagOutlined />
                )
              }
              onClick={() => handleFlagQuestion(currentQuestion?.id)}
              disabled={flaggingQuestion[currentQuestion?.id]}
              aria-label={flaggedQuestions.has(currentQuestion?.id) ? "Unflag this question" : "Flag this question for review"}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              {flaggingQuestion[currentQuestion?.id]
                ? flaggedQuestions.has(currentQuestion?.id)
                  ? "Unflagging…"
                  : "Flagging…"
                : flaggedQuestions.has(currentQuestion?.id)
                ? "Unflag"
                : "Flag"}
            </Button>
            {canVerifyWSQuestion && currentQuestion && (() => {
              const wsItemData = getWSItemData("question", wsVerifyBankId);
              const myVerdicts = wsItemData?.my_verdicts;
              const aggregate = wsItemData?.aggregate;
              const btnColors = getVerifierButtonColors(myVerdicts, aggregate);
              const isRejected = (myVerdicts?.web === "rejected" || myVerdicts?.mobile === "rejected" || (aggregate?.rejected ?? 0) > 0);
              return (
              <Tooltip title={isRejected ? "This question was rejected — review and re-approve (Tester mode)" : "Verify this question (Tester mode)"}>
              <Button
                size="small"
                variant={btnColors.variant}
                startIcon={<VerifiedUserIcon fontSize="small" />}
                onClick={() => {
                  loadWSVerifications("question", wsVerifyBankId);
                  setWsVerifyQuestionFull(null);
                  setWsVerifyQuestionLoading(true);
                  api.get(`/admin/question-bank/${wsVerifyBankId}`)
                    .then((r) => setWsVerifyQuestionFull(r.data?.data ?? null))
                    .catch(() => {})
                    .finally(() => setWsVerifyQuestionLoading(false));
                  setWsVerifyDialogOpen(true);
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  color: btnColors.color,
                  borderColor: "info.main",
                  bgcolor: btnColors.bgcolor,
                  "&:hover": { bgcolor: btnColors.variant === "contained" ? btnColors.bgcolor : "info.50" },
                }}
              >
                {formatVerifierButtonLabel(myVerdicts, aggregate)}
              </Button>
              </Tooltip>
              );
            })()}
            </Box>
          </Box>

          {/* Question type badge — all types */}
          {currentQuestion.question_type && (() => {
            const badge = getTypeBadgeProps(currentQuestion.question_type);
            if (!badge) return null;
            return (
              <Chip
                label={badge.label}
                size="small"
                variant="filled"
                sx={{
                  mb: 1,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                  bgcolor: badge.bg,
                  color: badge.color,
                  border: `1px solid ${badge.color}30`,
                }}
              />
            );
          })()}

          {/* Shared passage for non-RC English questions */}
          {sharedPassage && getQuestionCategory(currentQuestion.question_type) !== "reading_comprehension" &&
            renderPassageBox(sharedPassage, questions.map((q) => ({ question_text: q.name })))}

          {/* Question text — hidden for fill_blank (sentence shown inline below) */}
          {getQuestionCategory(currentQuestion.question_type) !== "fill_blank" && (
            <Box sx={{ mb: 2.5, lineHeight: 1.7, fontWeight: 500, fontSize: "1rem" }}>
              <HtmlWithLatex html={currentQuestion.name} />
            </Box>
          )}

          {/* Question image */}
          {questionImageUrl && (
            <Box
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                maxHeight: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "grey.50",
              }}
            >
              <Box
                component="img"
                src={questionImageUrl}
                alt="Question visual"
                sx={{ maxWidth: "100%", maxHeight: 320, objectFit: "contain", display: "block" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </Box>
          )}

          {/* Question input — type-aware */}
          {(() => {
            const category = getQuestionCategory(currentQuestion.question_type || "");

            if (category === "reading_comprehension") {
              const qId = currentQuestion.id;
              const passageText = currentQuestion.passage_text || currentQuestion.passage_stem || "";
              const audioUrl = currentQuestion.audio_url || "";
              const subQuestions = currentQuestion.ai_metadata?.sub_questions || [];
              let subAnswers = {};
              try {
                subAnswers = JSON.parse(textAnswers[qId] || "{}");
                if (!subAnswers || typeof subAnswers !== "object" || Array.isArray(subAnswers)) subAnswers = {};
              } catch {
                subAnswers = {};
              }

              const handleSubAnswer = (subIdx, optionText) => {
                const next = { ...subAnswers, [subIdx]: optionText };
                const json = JSON.stringify(next);
                setTextAnswers((p) => ({ ...p, [qId]: json }));
                setQuestionStatus((p) => ({
                  ...p,
                  [qId]: Object.values(next).some((v) => String(v || "").trim()) ? STATUS.ANSWERED : STATUS.UNANSWERED,
                }));
              };

              return (
                <Box>
                  {audioUrl && (
                    <Box sx={{ mb: 2 }}>
                      <audio controls src={audioUrl} style={{ width: "100%" }} />
                    </Box>
                  )}
                  {renderPassageBox(passageText, subQuestions.map((sq) => ({ question_text: sq.question })))}
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                    {currentQuestion.name || currentQuestion.question_text}
                  </Typography>
                  {subQuestions.map((sq, sqIdx) => (
                    <Box key={sqIdx} sx={{ mb: 2.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {sqIdx + 1}. {sq.question}
                      </Typography>
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          value={subAnswers[sqIdx] || subAnswers[String(sqIdx)] || ""}
                          onChange={(e) => handleSubAnswer(sqIdx, e.target.value)}
                        >
                          {(sq.options || []).map((opt, optIdx) => (
                            <FormControlLabel
                              key={optIdx}
                              value={opt}
                              control={<Radio size="small" />}
                              label={`${String.fromCharCode(65 + optIdx)}. ${opt}`}
                              sx={{ mb: 0.25, alignItems: "flex-start", "& .MuiFormControlLabel-label": { lineHeight: 1.5 } }}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              );
            }

            if (category === "writing") {
              const minWords = currentQuestion.ai_metadata?.min_words ?? 75;
              const maxWords = currentQuestion.ai_metadata?.max_words ?? 100;
              const writingInstructions = currentQuestion.ai_metadata?.writing_instructions || null;
              const whatToInclude = currentQuestion.ai_metadata?.what_to_include || [];
              const helpfulHints = currentQuestion.ai_metadata?.helpful_hints || [];
              const showInstructions   = currentQuestion.ai_metadata?.show_instructions   ?? true;
              const showWhatToInclude  = currentQuestion.ai_metadata?.show_what_to_include ?? true;
              const showHelpfulHints   = currentQuestion.ai_metadata?.show_helpful_hints   ?? true;
              const currentText = textAnswers[currentQuestion.id] || "";
              const wordCount = currentText.trim() ? currentText.trim().split(/\s+/).length : 0;
              const wordCountColor = wordCount === 0 ? "text.secondary" : wordCount < minWords ? "warning.main" : "success.main";
              return (
                <Box>
                  {/* Writing Topic */}
                  <Box sx={{ mb: 2, p: 2, bgcolor: "#e0f2fe", borderRadius: 2, borderLeft: "4px solid #0284c7" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#0369a1", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      ✏️ Writing Topic
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#0c4a6e", mt: 0.5, fontWeight: 500 }}>
                      {currentQuestion.name || currentQuestion.question_text}
                    </Typography>
                    {showInstructions && writingInstructions && (
                      <Typography variant="caption" sx={{ color: "#0369a1", mt: 0.5, display: "block", fontStyle: "italic" }}>
                        {writingInstructions}
                      </Typography>
                    )}
                  </Box>

                  {/* What to Include */}
                  {showWhatToInclude && whatToInclude.length > 0 && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "#fff7ed", borderRadius: 2, borderLeft: "4px solid #ea580c" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#c2410c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        📝 What to Include:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                        {whatToInclude.map((item, i) => (
                          <Typography key={i} component="li" variant="body2" sx={{ color: "#7c2d12", lineHeight: 1.7 }}>
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Helpful Hints */}
                  {showHelpfulHints && helpfulHints.length > 0 && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "#fefce8", borderRadius: 2, borderLeft: "4px solid #ca8a04" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#a16207", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        💡 Helpful Hints:
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {helpfulHints.map((hint, i) => (
                          <Typography key={i} variant="body2" sx={{ color: "#78350f", fontStyle: "italic", lineHeight: 1.8 }}>
                            "{hint}"
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Word count indicator */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: "#374151" }}>
                      📊 Suggested word count:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#2563eb" }}>
                      {minWords}–{maxWords} words
                    </Typography>
                    {wordCount > 0 && (
                      <Typography variant="caption" sx={{ color: wordCountColor, fontWeight: 600, ml: "auto" }}>
                        {wordCount} words written
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    multiline
                    minRows={8}
                    maxRows={20}
                    fullWidth
                    placeholder="Write your composition here..."
                    value={currentText}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTextAnswers((p) => ({ ...p, [currentQuestion.id]: val }));
                      setQuestionStatus((p) => ({
                        ...p,
                        [currentQuestion.id]: val.trim() ? STATUS.ANSWERED : STATUS.UNANSWERED,
                      }));
                    }}
                    sx={{ "& textarea": { lineHeight: 1.9 } }}
                  />
                  {wordCount > 0 && wordCount < minWords && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
                      {minWords - wordCount} more word{minWords - wordCount !== 1 ? "s" : ""} needed for full marks
                    </Typography>
                  )}
                </Box>
              );
            }

            if (category === "fill_blank") {
              const qId = currentQuestion.id;
              const placed = fillBlankAnswers[qId];
              const parts = parseBlanks(currentQuestion.name || "");
              const hasBlankToken = parts.some((p) => p.type === "blank");

              return (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                    Fill in the blank — click or drag a word from the word bank above
                  </Typography>
                  {/* Inline sentence with drop zone */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 0.5,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "grey.50",
                      border: "1.5px solid",
                      borderColor: placed ? "success.300" : "primary.200",
                      minHeight: 52,
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggingId) placeFillBlankAnswer(draggingId, qId);
                    }}
                  >
                    {parts.map((part, idx) => {
                      if (part.type === "text") {
                        return (
                          <Typography key={idx} variant="body1" sx={{ fontWeight: 500, lineHeight: 2 }}>
                            {part.value}
                          </Typography>
                        );
                      }
                      // blank slot
                      return (
                        <Box
                          key={idx}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            minWidth: 110,
                            height: 36,
                            px: 1.5,
                            borderRadius: 1.5,
                            border: "2px dashed",
                            borderColor: placed ? "success.main" : "primary.main",
                            bgcolor: placed ? "success.50" : "primary.50",
                            cursor: placed ? "default" : "pointer",
                            position: "relative",
                          }}
                          onClick={() => {
                            if (placed) {
                              releaseWord(qId);
                            } else {
                              const available = wordBank.find((w) => w.usedByQId === null);
                              if (available) placeFillBlankAnswer(available.instanceId, qId);
                            }
                          }}
                        >
                          {placed ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", justifyContent: "space-between" }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "success.dark" }}>
                                {placed.word}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "success.main", cursor: "pointer", fontWeight: 700, lineHeight: 1 }}
                                title="Remove"
                              >
                                ✕
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                              drop here
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                    {/* If no blank token in sentence, append drop zone after text */}
                    {!hasBlankToken && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          minWidth: 110,
                          height: 36,
                          px: 1.5,
                          borderRadius: 1.5,
                          border: "2px dashed",
                          borderColor: placed ? "success.main" : "primary.main",
                          bgcolor: placed ? "success.50" : "primary.50",
                          cursor: placed ? "default" : "pointer",
                        }}
                        onClick={() => {
                          if (placed) {
                            releaseWord(qId);
                          } else {
                            const available = wordBank.find((w) => w.usedByQId === null);
                            if (available) placeFillBlankAnswer(available.instanceId, qId);
                          }
                        }}
                      >
                        {placed ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", justifyContent: "space-between" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "success.dark" }}>
                              {placed.word}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "success.main", fontWeight: 700 }}>✕</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                            drop here
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                  {placed && (
                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: "block" }}>
                      Click the word or the ✕ to remove and choose a different word.
                    </Typography>
                  )}
                  {isTestMode && verifyResults[qId] && (
                    <Alert severity={!verifyResults[qId].was_answered ? "info" : verifyResults[qId].is_correct ? "success" : "error"} sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                      {verifyResults[qId].correct_answer
                        ? <Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                            {verifyResults[qId].was_answered && <span style={{ fontWeight: 700 }}>{verifyResults[qId].is_correct ? "Correct! ✓" : "Incorrect"} —</span>}
                            <span>Correct answer:</span>
                            <HtmlWithLatex html={verifyResults[qId].correct_answer} />
                          </Box>
                        : (verifyResults[qId].was_answered ? (verifyResults[qId].is_correct ? "Correct! ✓" : "Incorrect") : "No answer selected")}
                    </Alert>
                  )}
                  {isTestMode && verifyResults[qId] && renderExplanation(qId, getExplanationForQuestion(qId, verifyResults[qId].explanation))}
                </Box>
              );
            }

            if (category === "short_answer") {
              // Detect maths-style short answer (hyphenated, from uk11plus config)
              // Show math symbol bar only for Maths subject short-answer questions
              const isMathsShortAnswer = isMathsTest && (currentQuestion.question_type || "").trim() === "Short-answer";
              const insertSymbol = (sym) => {
                const qId = currentQuestion.id;
                const current = textAnswers[qId] || "";
                const updated = current + sym;
                setTextAnswers((p) => ({ ...p, [qId]: updated }));
                setQuestionStatus((p) => ({
                  ...p,
                  [qId]: updated.trim() ? STATUS.ANSWERED : STATUS.UNANSWERED,
                }));
              };
              return (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Short Answer — write your answer below
                  </Typography>
                  {isMathsShortAnswer && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                      {MATH_SYMBOLS.map((sym) => (
                        <Box
                          key={sym}
                          onClick={() => insertSymbol(sym)}
                          sx={{
                            px: 1, py: 0.4,
                            border: "1px solid #d1d5db",
                            borderRadius: 1,
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            fontFamily: "monospace",
                            bgcolor: "#f9fafb",
                            userSelect: "none",
                            "&:hover": { bgcolor: "#e0f2fe", borderColor: "#0369a1" },
                          }}
                        >
                          {sym}
                        </Box>
                      ))}
                    </Box>
                  )}
                  <TextField
                    multiline
                    minRows={isMathsShortAnswer ? 2 : 3}
                    fullWidth
                    placeholder="Write your answer…"
                    value={textAnswers[currentQuestion.id] || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTextAnswers((p) => ({ ...p, [currentQuestion.id]: val }));
                      const opt = currentQuestion.options?.[0];
                      if (opt && val.trim().toLowerCase() === String(opt.name).trim().toLowerCase()) {
                        handleAnswerChange(currentQuestion.id, String(opt.id));
                      } else {
                        setQuestionStatus((p) => ({
                          ...p,
                          [currentQuestion.id]: val.trim() ? STATUS.ANSWERED : STATUS.UNANSWERED,
                        }));
                      }
                    }}
                  />
                  {isTestMode && verifyResults[currentQuestion.id] && (
                    <Alert severity={!verifyResults[currentQuestion.id].was_answered ? "info" : verifyResults[currentQuestion.id].is_correct ? "success" : "error"} sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                      {verifyResults[currentQuestion.id].correct_answer
                        ? <Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                            {verifyResults[currentQuestion.id].was_answered && <span style={{ fontWeight: 700 }}>{verifyResults[currentQuestion.id].is_correct ? "Correct! ✓" : "Incorrect"} —</span>}
                            <span>Correct answer:</span>
                            <HtmlWithLatex html={verifyResults[currentQuestion.id].correct_answer} />
                          </Box>
                        : (verifyResults[currentQuestion.id].was_answered ? (verifyResults[currentQuestion.id].is_correct ? "Correct! ✓" : "Incorrect") : "No answer selected")}
                    </Alert>
                  )}
                  {isTestMode && verifyResults[currentQuestion.id] && renderExplanation(currentQuestion.id, getExplanationForQuestion(currentQuestion.id, verifyResults[currentQuestion.id].explanation))}
                </Box>
              );
            }

            if (category === "vocab_matching") {
              const qId = currentQuestion.id;
              const opts = currentQuestion.options || [];

              // Compute a stable shuffled order for definitions once per question
              if (!shuffledDefsRef.current[qId]) {
                const indices = opts.map((_, i) => i);
                for (let i = indices.length - 1; i > 0; i--) {
                  const j = Math.floor(Math.random() * (i + 1));
                  [indices[i], indices[j]] = [indices[j], indices[i]];
                }
                shuffledDefsRef.current[qId] = indices;
              }
              const shuffledIndices = shuffledDefsRef.current[qId];
              const matches = vocabMatches[qId] || {};         // { wordOptId: shuffledDefPos }
              const selectedWordId = selectedVocabWord[qId] || null;
              // Also track if a definition is selected first (bidirectional matching)
              const selectedDefPos = selectedVocabWord[`${qId}_def`] ?? null;

              // Reverse map: shuffledDefPos → wordOptId (to colour definition boxes)
              const defPosToWordId = {};
              Object.entries(matches).forEach(([wId, pos]) => { defPosToWordId[pos] = wId; });

              // Assign a colour to each matched pair in insertion order + pair number
              const matchedWordIdList = Object.keys(matches);
              const getMatchColor = (wordOptId) => {
                const idx = matchedWordIdList.indexOf(String(wordOptId));
                return idx >= 0 ? VOCAB_MATCH_COLORS[idx % VOCAB_MATCH_COLORS.length] : null;
              };
              const getMatchNumber = (wordOptId) => {
                const idx = matchedWordIdList.indexOf(String(wordOptId));
                return idx >= 0 ? idx + 1 : null;
              };

              // ── Click handlers ──
              const handleWordClick = (optId) => {
                const sId = String(optId);
                // If a definition is already selected, complete the match
                if (selectedDefPos !== null) {
                  setVocabMatches((p) => {
                    const cur = { ...(p[qId] || {}) };
                    Object.keys(cur).forEach((wId) => { if (cur[wId] === selectedDefPos) delete cur[wId]; });
                    delete cur[sId];
                    cur[sId] = selectedDefPos;
                    return { ...p, [qId]: cur };
                  });
                  setSelectedVocabWord((p) => ({ ...p, [qId]: null, [`${qId}_def`]: null }));
                  setQuestionStatus((p) => ({ ...p, [qId]: STATUS.ANSWERED }));
                } else {
                  // Toggle word selection
                  setSelectedVocabWord((p) => ({ ...p, [qId]: p[qId] === sId ? null : sId, [`${qId}_def`]: null }));
                }
              };

              const handleDefClick = (shuffledPos) => {
                // If a word is already selected, complete the match
                if (selectedWordId) {
                  setVocabMatches((p) => {
                    const cur = { ...(p[qId] || {}) };
                    Object.keys(cur).forEach((wId) => { if (cur[wId] === shuffledPos) delete cur[wId]; });
                    delete cur[selectedWordId];
                    cur[selectedWordId] = shuffledPos;
                    return { ...p, [qId]: cur };
                  });
                  setSelectedVocabWord((p) => ({ ...p, [qId]: null, [`${qId}_def`]: null }));
                  setQuestionStatus((p) => ({ ...p, [qId]: STATUS.ANSWERED }));
                } else {
                  // Toggle definition selection (bidirectional)
                  setSelectedVocabWord((p) => ({
                    ...p,
                    [`${qId}_def`]: p[`${qId}_def`] === shuffledPos ? null : shuffledPos,
                    [qId]: null,
                  }));
                }
              };

              const handleUnmatch = (wordOptId) => {
                setVocabMatches((p) => {
                  const cur = { ...(p[qId] || {}) };
                  delete cur[String(wordOptId)];
                  const remaining = Object.keys(cur).length;
                  setQuestionStatus((ps) => ({
                    ...ps,
                    [qId]: remaining > 0 ? STATUS.ANSWERED : STATUS.UNANSWERED,
                  }));
                  return { ...p, [qId]: cur };
                });
                setSelectedVocabWord((p) => ({ ...p, [qId]: null, [`${qId}_def`]: null }));
              };

              const matchedCount = Object.keys(matches).length;
              const allMatched = matchedCount === opts.length;
              const hasSelection = !!selectedWordId || selectedDefPos !== null;
              // Compact mode for 6+ pairs
              const isCompact = opts.length >= 6;
              const boxPy = isCompact ? 0.7 : 1.2;
              const boxMb = isCompact ? 0.5 : 0.75;
              const fontSize = isCompact ? "0.82rem" : "0.9rem";

              return (
                <Box>
                  {/* Verify results summary */}
                  {isTestMode && verifyResults[qId] && (
                    <Alert severity={verifyResults[qId].is_correct ? "success" : "error"} sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}>
                      {verifyResults[qId].is_correct
                        ? "All pairs correct!"
                        : `${verifyResults[qId].pairs_correct}/${verifyResults[qId].pairs_total} pairs correct — incorrect pairs shown below`}
                    </Alert>
                  )}
                  {isTestMode && verifyResults[qId] && renderExplanation(qId, getExplanationForQuestion(qId, verifyResults[qId].explanation))}

                  {/* Progress bar row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Match all pairs
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: allMatched ? "success.main" : "text.secondary" }}>
                      {matchedCount}/{opts.length} matched
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={opts.length > 0 ? (matchedCount / opts.length) * 100 : 0}
                    sx={{
                      mb: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "#e0f2fe",
                      "& .MuiLinearProgress-bar": { bgcolor: allMatched ? "success.main" : "primary.main", borderRadius: 3 },
                    }}
                  />

                  {/* Dynamic instruction */}
                  <Box sx={{
                    textAlign: "center", mb: 1.5, py: 0.75, px: 2, borderRadius: 2,
                    bgcolor: hasSelection ? "#eff6ff" : allMatched ? "#f0fdf4" : "#f8fafc",
                    border: "1px solid",
                    borderColor: hasSelection ? "#bfdbfe" : allMatched ? "#bbf7d0" : "#e2e8f0",
                    transition: "all 0.2s",
                  }}>
                    <Typography variant="body2" sx={{
                      fontWeight: 600, fontSize: "0.85rem",
                      color: hasSelection ? "primary.main" : allMatched ? "success.dark" : "text.secondary",
                    }}>
                      {allMatched
                        ? "All pairs matched! Review or click a pair to unmatch."
                        : hasSelection
                        ? (selectedWordId ? "Now click the matching definition" : "Now click the matching word")
                        : "Click a word or definition to start matching"}
                    </Typography>
                  </Box>

                  {/* Always show full vocab list without internal vertical scroll */}
                  <Box sx={{
                    display: "grid", gridTemplateColumns: "1fr 1fr", gap: isCompact ? 1.5 : 2,
                  }}>
                    {/* ── Words column (original order) ── */}
                    <Box>
                      <Typography variant="caption" sx={{
                        fontWeight: 700, mb: 0.75, display: "block", textAlign: "center",
                        color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.05em",
                      }}>
                        Words
                      </Typography>
                      {opts.map((opt) => {
                        const sId = String(opt.id);
                        const isSelected = selectedWordId === sId;
                        const isMatched = sId in matches;
                        const color = isMatched ? getMatchColor(sId) : null;
                        const pairNum = isMatched ? getMatchNumber(sId) : null;
                        const isClickTarget = selectedDefPos !== null && !isMatched;
                        // Verify feedback for this word
                        const vqr = verifyResults[qId];
                        const pairResult = vqr?.pair_results?.find((pr) => String(pr.word_option_id) === sId);
                        const isVerifiedCorrect = isTestMode && pairResult?.is_correct === true;
                        const isVerifiedWrong = isTestMode && pairResult && pairResult.is_correct === false;
                        return (
                          <Box key={opt.id}>
                            <Box
                              onClick={() => isMatched ? handleUnmatch(opt.id) : handleWordClick(opt.id)}
                              sx={{
                                py: boxPy, px: 1.5, mb: 0.25,
                                border: "2px solid",
                                borderRadius: 2,
                                cursor: "pointer",
                                userSelect: "none",
                                textAlign: "center",
                                position: "relative",
                                borderColor: isSelected ? "#2563eb"
                                  : isVerifiedCorrect ? "success.main"
                                  : isVerifiedWrong ? "error.main"
                                  : isMatched ? color.border
                                  : isClickTarget ? "#93c5fd" : "#e2e8f0",
                                bgcolor: isSelected ? "#dbeafe"
                                  : isVerifiedCorrect ? "#f0fdf4"
                                  : isVerifiedWrong ? "#fef2f2"
                                  : isMatched ? color.bg
                                  : isClickTarget ? "#eff6ff" : "background.paper",
                                transition: "all 0.2s ease",
                                boxShadow: isSelected ? "0 0 0 3px rgba(37,99,235,0.2)" : isMatched ? `0 0 0 1px ${color.border}33` : "none",
                                "&:hover": {
                                  borderColor: isMatched ? color.border : "#2563eb",
                                  boxShadow: isMatched
                                    ? `0 0 0 2px ${color.border}44`
                                    : "0 0 0 3px rgba(37,99,235,0.15)",
                                  transform: "translateY(-1px)",
                                },
                              }}
                            >
                              {/* Pair number badge */}
                              {isMatched && (
                                <Box sx={{
                                  position: "absolute", top: -8, left: -8,
                                  width: 20, height: 20, borderRadius: "50%",
                                  bgcolor: color.border, color: "#fff",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: "0.65rem", fontWeight: 800,
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                }}>
                                  {pairNum}
                                </Box>
                              )}
                              <Typography sx={{
                                fontSize, fontWeight: isSelected || isMatched ? 700 : 500,
                                color: isVerifiedCorrect ? "success.dark"
                                  : isVerifiedWrong ? "error.dark"
                                  : isMatched ? color.text
                                  : isSelected ? "#1d4ed8" : "text.primary",
                              }}>
                                {opt.name}
                              </Typography>
                            </Box>
                            {/* Correct definition for wrong pairs */}
                            {isVerifiedWrong && pairResult.correct_def && (
                              <Typography variant="caption" sx={{
                                display: "block", mb: boxMb, px: 1, color: "error.dark", fontStyle: "italic",
                              }}>
                                ✓ {pairResult.correct_def}
                              </Typography>
                            )}
                            {!isVerifiedWrong && <Box sx={{ mb: boxMb }} />}
                          </Box>
                        );
                      })}
                    </Box>

                    {/* ── Definitions column (shuffled) ── */}
                    <Box>
                      <Typography variant="caption" sx={{
                        fontWeight: 700, mb: 0.75, display: "block", textAlign: "center",
                        color: "#047857", textTransform: "uppercase", letterSpacing: "0.05em",
                      }}>
                        Definitions
                      </Typography>
                      {shuffledIndices.map((origIdx, shuffledPos) => {
                        const opt = opts[origIdx];
                        const matchedWordId = defPosToWordId[shuffledPos];
                        const isMatched = matchedWordId !== undefined;
                        const isSelected = selectedDefPos === shuffledPos;
                        const color = isMatched ? getMatchColor(matchedWordId) : null;
                        const pairNum = isMatched ? getMatchNumber(matchedWordId) : null;
                        // When a word is selected, highlight unmatched definitions as clickable
                        const isClickTarget = !!selectedWordId && !isMatched;
                        return (
                          <Box
                            key={origIdx}
                            onClick={() => {
                              if (isMatched) handleUnmatch(matchedWordId);
                              else handleDefClick(shuffledPos);
                            }}
                            sx={{
                              py: boxPy, px: 1.5, mb: boxMb,
                              border: "2px solid",
                              borderRadius: 2,
                              cursor: "pointer",
                              userSelect: "none",
                              textAlign: "center",
                              position: "relative",
                              borderColor: isSelected ? "#059669" : isMatched ? color.border : isClickTarget ? "#6ee7b7" : "#e2e8f0",
                              bgcolor: isSelected ? "#d1fae5" : isMatched ? color.bg : isClickTarget ? "#ecfdf5" : "#f8fafc",
                              transition: "all 0.2s ease",
                              boxShadow: isSelected ? "0 0 0 3px rgba(5,150,105,0.2)" : isMatched ? `0 0 0 1px ${color.border}33` : "none",
                              "&:hover": {
                                borderColor: isMatched ? color.border : "#059669",
                                boxShadow: isMatched
                                  ? `0 0 0 2px ${color.border}44`
                                  : "0 0 0 3px rgba(5,150,105,0.15)",
                                transform: "translateY(-1px)",
                              },
                            }}
                          >
                            {/* Pair number badge for matched items */}
                            {isMatched && (
                              <Box sx={{
                                position: "absolute", top: -8, right: -8,
                                width: 20, height: 20, borderRadius: "50%",
                                bgcolor: color.border, color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "0.65rem", fontWeight: 800,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                              }}>
                                {pairNum}
                              </Box>
                            )}
                            <Typography sx={{
                              fontSize, fontWeight: isSelected || isMatched ? 700 : 500,
                              color: isMatched ? color.text : isSelected ? "#065f46" : "text.primary",
                            }}>
                              {opt.match_text || opt.name}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Tip for unmatching */}
                  {matchedCount > 0 && !allMatched && (
                    <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 1, color: "text.disabled" }}>
                      Click any coloured pair to unmatch it
                    </Typography>
                  )}
                </Box>
              );
            }

            // Default: MCQ radio buttons
            const isDiagram = (currentQuestion.question_type || "").toLowerCase().includes("diagram");
            const vr = verifyResults[currentQuestion.id];
            return (
              <>
                {isDiagram && (
                  <Alert severity="info" icon={<Info sx={{ fontSize: 18 }} />} sx={{ mb: 2, py: 0.5, fontSize: "0.82rem" }}>
                    Refer to the diagram or image above to answer this question.
                  </Alert>
                )}
                {isTestMode && vr && (
                  <Alert severity={!vr.was_answered ? "info" : vr.is_correct ? "success" : "error"} sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}>
                    {vr.correct_answer
                      ? <Box sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                          {vr.was_answered && <span style={{ fontWeight: 700 }}>{vr.is_correct ? "Correct! ✓" : "Incorrect"} —</span>}
                          <span>Correct answer:</span>
                          <HtmlWithLatex html={vr.correct_answer} />
                        </Box>
                      : (vr.was_answered ? (vr.is_correct ? "Correct! ✓" : "Incorrect") : "No answer selected")}
                  </Alert>
                )}
                {isTestMode && vr && renderExplanation(currentQuestion.id, getExplanationForQuestion(currentQuestion.id, vr.explanation))}
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  >
                    {currentQuestion.options.map((opt) => {
                      const isSelected = String(opt.id) === String(answers[currentQuestion.id]);
                      const isVerifiedCorrect = vr && vr.correct_answer === opt.letter;
                      const isVerifiedWrong = vr && isSelected && !vr.is_correct;
                      return (
                        <Paper
                          key={opt.id}
                          sx={{
                            p: 2,
                            mb: 2,
                            border: isTestMode && vr && isVerifiedCorrect
                              ? "2px solid #4caf50"
                              : isTestMode && vr && isVerifiedWrong
                              ? "2px solid #f44336"
                              : isSelected
                              ? "2px solid #667eea"
                              : "1px solid #e0e0e0",
                            borderRadius: 2,
                            cursor: "pointer",
                            bgcolor: isTestMode && vr && isVerifiedCorrect
                              ? "#e8f5e9"
                              : isTestMode && vr && isVerifiedWrong
                              ? "#ffebee"
                              : isSelected ? "primary.50" : "transparent",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              transform: "translateY(-1px)",
                            },
                          }}
                          onClick={() => handleAnswerChange(currentQuestion.id, String(opt.id))}
                        >
                          <FormControlLabel
                            value={String(opt.id)}
                            control={<Radio size="small" />}
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.95rem" }}>
                                  <Typography component="span" sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
                                    <strong>{opt.letter}.</strong>
                                  </Typography>
                                  <HtmlWithLatex html={opt.name} />
                                </Box>
                                {isTestMode && vr && isVerifiedCorrect && (
                                  <CheckCircle sx={{ fontSize: 18, color: "success.main" }} />
                                )}
                                {isTestMode && vr && isVerifiedWrong && (
                                  <Typography sx={{ fontSize: 16, color: "error.main", fontWeight: 700 }}>✗</Typography>
                                )}
                              </Box>
                            }
                            sx={{ width: "100%", m: 0 }}
                          />
                        </Paper>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Selected:{" "}
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {selectedLetter}
                  </Typography>
                </Typography>
              </>
            );
          })()}

        </CardContent>
      </Card>

      {/* Navigation Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              startIcon={<NavigateBefore />}
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Previous
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              {currentQuestionIndex < questions.length - 1 ? (
                <Button
                  endIcon={<NavigateNext />}
                  onClick={handleNextQuestion}
                  variant="contained"
                  sx={{ borderRadius: 2, px: 4 }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitTest}
                  disabled={submitting || getAnsweredCount() === 0}
                  sx={{
                    background: "linear-gradient(90deg, #1a1a1a 0%, #3a3a3a 100%)",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 4,
                    "&.Mui-disabled": { background: "#d1d5db", color: "#374151" },
                  }}
                >
                  {submitting ? "Submitting…" : isTestMode ? "Finish Practice" : "Submit Work Sheet"}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Processing dialog */}
      {showProcessing && (
        <Dialog open fullWidth maxWidth="xs">
          <DialogContent sx={{ textAlign: "center", py: 5 }}>
            {processingStep === "processing" && (
              <>
                <CircularProgress size={52} sx={{ mb: 2.5, color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Submitting your answers…
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Please wait a moment.
                </Typography>
              </>
            )}
            {processingStep === "success" && (
              <>
                <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Submitted Successfully!
                </Typography>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Results dialog */}
      <Dialog open={showResultsDialog} onClose={handleCloseResults} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Work Sheet Results</DialogTitle>
        <DialogContent>
          {testResults && (() => {
            const pct = testResults.summary?.percentage ?? 0;
            const correct = testResults.summary?.correct_answers ?? 0;
            const total = testResults.summary?.total_questions ?? 0;
            const gradable = testResults.summary?.gradable_total ?? total;
            const writingCount = testResults.summary?.writing_count ?? 0;
            const gradableMarks = testResults.summary?.gradable_marks ?? null;
            const perf = getPerformanceLevel(pct);
            const correctDisplay = Number.isInteger(correct) ? correct : correct.toFixed(1);
            // Estimated marks earned (proportional to correct / gradable ratio applied to gradable marks)
            const estimatedMarks = gradableMarks != null && gradable > 0
              ? Math.round((correct / gradable) * gradableMarks * 10) / 10
              : null;
            return (
              <Box sx={{ py: 1 }}>
                {/* Score display */}
                <Box
                  sx={{
                    textAlign: "center",
                    py: 3,
                    px: 2,
                    mb: 2.5,
                    borderRadius: 3,
                    background: pct >= 50
                      ? "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)"
                      : "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                    border: "1px solid",
                    borderColor: pct >= 50 ? "#6ee7b7" : "#fca5a5",
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      color: pct >= 50 ? "#065f46" : "#991b1b",
                      lineHeight: 1,
                      mb: 0.5,
                    }}
                  >
                    {pct}%
                  </Typography>
                  {estimatedMarks != null ? (
                    <Typography variant="body1" sx={{ fontWeight: 600, color: pct >= 50 ? "#047857" : "#b91c1c" }}>
                      ~{estimatedMarks} / {gradableMarks} marks
                    </Typography>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 600, color: pct >= 50 ? "#047857" : "#b91c1c" }}>
                      {correctDisplay} / {gradable} correct
                    </Typography>
                  )}
                  {writingCount > 0 && (
                    <Typography variant="caption" sx={{ color: pct >= 50 ? "#047857" : "#b91c1c", opacity: 0.8 }}>
                      ({writingCount} writing {writingCount === 1 ? "question" : "questions"} submitted — not auto-graded)
                    </Typography>
                  )}
                </Box>

                {/* Performance chip */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Chip
                    icon={<EmojiEvents />}
                    label={perf.label}
                    sx={{
                      bgcolor: perf.bgcolor,
                      color: perf.color,
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      height: 36,
                      px: 1,
                    }}
                  />
                </Box>

                {/* Stats row */}
                <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#e8f5e9",
                      border: "1px solid #a5d6a7",
                    }}
                  >
                    <CheckCircleOutline sx={{ color: "#2e7d32", fontSize: 20, mb: 0.25 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                      {correctDisplay}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Correct</Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#ffebee",
                      border: "1px solid #ef9a9a",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#c62828" }}>
                      {Number.isInteger(gradable - correct) ? gradable - correct : (gradable - correct).toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Incorrect</Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#f3f4f6",
                      border: "1px solid #d1d5db",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#374151" }}>
                      {gradableMarks != null ? `${gradableMarks}` : gradable}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {gradableMarks != null ? "Gradable Marks" : "Gradable"}
                    </Typography>
                  </Box>
                </Box>

                {writingCount > 0 && (
                  <Alert severity="warning" sx={{ borderRadius: 2, mb: 1.5 }}>
                    {writingCount} writing {writingCount === 1 ? "composition was" : "compositions were"} submitted for manual review.
                  </Alert>
                )}

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Use <strong>Sweep</strong> on the Work Sheets page to retake incorrect questions and improve your score.
                </Alert>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={handleCloseResults} variant="outlined" sx={{ borderRadius: 2, fontWeight: 600 }}>
            Back to Work Sheets
          </Button>
          <Button
            onClick={handleViewReport}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            View Report
          </Button>
        </DialogActions>
      </Dialog>
      {/* ---- Flag for Review Dialog ---- */}
      <Dialog
        open={wsFlagDialog.open}
        onClose={() => setWsFlagDialog(EMPTY_WS_FLAG_DIALOG)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", pb: 0 }}>Flag for Review</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select the issue type(s) and/or add a brief reason — all optional.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", mb: 1.5 }}>
            {[
              { key: "issue_question", label: "Question issue" },
              { key: "issue_answer",   label: "Answer issue"  },
              { key: "issue_image",    label: "Image issue"   },
            ].map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    size="small"
                    checked={!!wsFlagDialog[key]}
                    onChange={(e) => setWsFlagDialog((prev) => ({ ...prev, [key]: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">{label}</Typography>}
                sx={{ ml: 0 }}
              />
            ))}
          </Box>
          <Box sx={{ '& .ql-container': { minHeight: 100, fontSize: '0.9rem' }, '& .ql-editor.ql-blank::before': { fontStyle: 'normal', color: '#9e9e9e' } }}>
            <ReactQuill
              theme="snow"
              value={wsFlagDialog.reason}
              onChange={(html) => processFlagReasonHtml(html, (v) => setWsFlagDialog((prev) => ({ ...prev, reason: v })))}
              modules={FLAG_QUILL_MODULES}
              formats={FLAG_QUILL_FORMATS}
              placeholder="e.g. Unclear wording, possible typo, wrong answer..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button onClick={() => setWsFlagDialog(EMPTY_WS_FLAG_DIALOG)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleWsFlagSave}
            disabled={!!flaggingQuestion[wsFlagDialog.questionId]}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {flaggingQuestion[wsFlagDialog.questionId] ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      {canVerifyWSQuestion && currentQuestion && (
        <QuestionVerifyDialog
          open={wsVerifyDialogOpen}
          question={wsVerifyQuestionFull ?? currentQuestion}
          loading={wsVerifyQuestionLoading}
          onClose={() => setWsVerifyDialogOpen(false)}
          onSubmit={async ({ status, notes, platforms }) => {
            const ok = await submitWSVerification({ type: "question", id: wsVerifyBankId, status, notes, platforms });
            if (ok) {
              toast.success(`Question marked as ${status}.`);
              setWsVerifyDialogOpen(false);
            } else {
              toast.error("Failed to save verification.");
            }
          }}
          onEdit={wsVerifyQuestionFull ? () => setWsQbEditOpen(true) : undefined}
          itemData={getWSItemData("question", wsVerifyBankId)}
          submitting={wsVerifySubmitting}
        />
      )}
      {wsQbEditOpen && wsVerifyQuestionFull && (
        <QuestionBankQuestionEditDialog
          question={wsVerifyQuestionFull}
          onClose={() => setWsQbEditOpen(false)}
          saving={wsQbEditSaving}
          onSave={async (payload) => {
            const savedQuestionIndex = currentQuestionIndex;
            setWsQbEditSaving(true);
            try {
              const res = await api.put(`/admin/question-bank/${wsVerifyQuestionFull.id}`, payload);
              setWsVerifyQuestionFull(res.data?.data ?? wsVerifyQuestionFull);
              await refreshWorksheetQuestions();
              setCurrentQuestionIndex(savedQuestionIndex);
              setWsQbEditOpen(false);
            } catch (err) {
              const msg = err?.response?.data?.message || "Failed to save question.";
              toast.error(msg);
            } finally {
              setWsQbEditSaving(false);
            }
          }}
        />
      )}
    </Container>
  );
};

export default WorkSheetTake;
